import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { ATitle } from '../../../components';
import {
  ArrowLeftOutlined,
  FormOutlined,
  SignatureOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import {
  IProfessional,
  ITerminology,
  ISpecialty,
} from '../../../shared/interfaces';
import { Breadcrumb, Button, Flex, Spin, Tabs, Typography } from 'antd';
import FormDataProfessional from './FormData';
import ProfessionalSignature from './Signature';
import { getProfessional, getSelectSpecialties } from '../../../http';

const ProfessionalCreateEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [professional, setProfessional] = useState<IProfessional | null>(null);
  const [terminologies, setTerminologies] = useState<ITerminology[]>([]);
  const [specialties, setSpecialties] = useState<ISpecialty[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const isNew = id === 'new';

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const data = await getProfessional(id);
      setProfessional(data);
    } finally {
      setLoadingData(false);
    }
  }, [id]);

  const loadTerminologies = useCallback(async () => {
    try {
      const response = await api.get(`/terminologies/select`);
      setTerminologies(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar terminologias',
      });
    }
  }, [addToast]);

  const loadSpecialties = useCallback(async () => {
    try {
      const data = await getSelectSpecialties();
      setSpecialties(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar especialidades',
      });
    }
  }, [addToast]);

  useEffect(() => {
    if (!isNew) {
      loadData();
    }
    loadTerminologies();
    loadSpecialties();
  }, [id]);

  return (
    <>
      <Breadcrumb
        style={{ marginBottom: 8 }}
        items={[
          {
            title: (
              <Typography.Link onClick={() => navigate('/professionals')}>
                Profissionais
              </Typography.Link>
            ),
          },
          {
            title: isNew ? 'Novo Profissional' : 'Editar Profissional',
          },
        ]}
      />

      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/professionals')}
        />
        <ATitle icon={<TeamOutlined />}>
          {isNew
            ? 'Novo Profissional'
            : loadingData
              ? 'Carregando...'
              : `Editar: ${professional?.name}`}
        </ATitle>
      </Flex>

      <Spin spinning={loadingData && !isNew}>
        <Tabs
          type="card"
          items={[
            {
              label: (
                <Flex align="center" gap={6}>
                  <FormOutlined />
                  <span>Dados do Profissional</span>
                </Flex>
              ),
              key: '1',
              children: (
                <FormDataProfessional
                  professional={professional}
                  specialties={specialties}
                  terminologies={terminologies}
                />
              ),
            },
            {
              label: (
                <Flex align="center" gap={6}>
                  <SignatureOutlined />
                  <span>Assinatura Digital</span>
                </Flex>
              ),
              key: '2',
              disabled: isNew,
              children: <ProfessionalSignature professional={professional} />,
            },
          ]}
        />
      </Spin>
    </>
  );
};

export default ProfessionalCreateEdit;
